import { ArrowRight, NotebookPen, Sparkles, Timer } from 'lucide-react';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Dispatch, FC, SetStateAction, useState } from 'react';
import { useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import onboardingGif from '../../assets/diagnsoticOnboardingAnimation.gif';
import { DiagnosticOnboardingPill } from './DiagnosticOnboardingPill';
import { DiagnosticSteps } from '../../models/DiagnosticStepsEnum';
import { DiagnosticStateEnum } from './Diagnostic';
import { ResumeDiagnosticCard } from './ResumeDiagnosticCard';
import { MaskedStudentData } from '../MaskedData/MaskedStudentData';
import { PiiTypes } from '../../analytics/dataMasking';
import { requestFullscreen } from '../../utils/fullscreen';
import { usePreviewMode } from '../../contexts/PreviewModeContext';

interface Props {
  setStepDiagnostic: Dispatch<SetStateAction<DiagnosticSteps>>;
  diagnosticState: DiagnosticStateEnum | null;
  setDiagnosticState: Dispatch<SetStateAction<DiagnosticStateEnum | null>>;
  refechSessionInfo: () => void;
}

export const DiagnosticPresentation: FC<Props> = ({
  setStepDiagnostic,
  diagnosticState,
  setDiagnosticState,
  refechSessionInfo,
}) => {
  const userRedux = useAppSelector(selectUser);
  const [step, setStep] = useState(0);
  const previewMode = usePreviewMode();

  const renderInnerContent = () => {
    const options = [
      {
        header: (
          <h1 className="tw-text-center tw-mt-5 tw-text-[2rem]">
            Hello,{' '}
            <span className="tw-text-pink-500">
              <MaskedStudentData student={{ publicUserId: userRedux?.sub }} type={PiiTypes.FirstName}>
                {userRedux?.firstName}
              </MaskedStudentData>
            </span>
            !
          </h1>
        ),
        content: (
          <p
            className="tw-text-[1.5rem] tw-text-center tw-w-[70%] tw-mx-auto"
            style={{ fontFamily: 'Atkinson Hyperlegible', lineHeight: '1.7rem' }}
          >
            This is your <b>personal math journey.</b> Every student gets a unique path, and yours is about to begin!
          </p>
        ),
        btnLabel: 'Next',
        command: () => setStep(step => step + 1),
      },
      {
        header: (
          <>
            <h1 className="tw-text-center tw-text-[2rem] tw-mb-0 tw-pb-0">
              To <b>create your own roadmap</b>
            </h1>
            <h1 className="tw-text-center tw-text-[2rem] tw-text-[1rem] tw-font-normal tw-my-0 tw-py-0">
              we need to know about your math skills
            </h1>
          </>
        ),
        content: (
          <div className="tw-flex tw-justify-center">
            <img src={onboardingGif} alt="Onboarding" style={{ width: '22rem', height: '10rem' }} />
          </div>
        ),
        btnLabel: 'Next',
        command: () => setStep(step => step + 1),
      },
      {
        header: <h1 className="tw-text-center tw-text-[2rem]">Show us what you know</h1>,
        content: (
          <div className="">
            <p
              className="tw-text-[1.3rem] tw-text-center tw-w-[90%] tw-mt-3 tw-mb-0 tw-py-0 tw-mx-auto"
              style={{ fontFamily: 'Atkinson Hyperlegible', lineHeight: '1.2rem' }}
            >
              {`Some questions will be challenging, that's okay!`}
            </p>
            <p
              className="tw-text-[1.3rem] tw-text-center tw-w-[90%] tw-mt-2 tw-pt-0 tw-mx-auto"
              style={{ fontFamily: 'Atkinson Hyperlegible', lineHeight: '1.2rem' }}
            >
              {`It gets harder on purpose to find your gaps. Don't
              stress.`}
            </p>
            <div className="tw-w-[70%] tw-mx-auto tw-py-4">
              <div className="tw-mx-auto tw-w-[70%]">
                <DiagnosticOnboardingPill
                  label="Keep a pencil & paper nearby"
                  colorClass="tw-bg-indigo-50 tw-border-indigo-200"
                  icon={
                    <NotebookPen
                      size={20}
                      className="tw-text-indigo-500 tw-mx-2 tw-bg-indigo-200 tw-rounded-full tw-p-1 tw-w-[1.5rem] tw-h-[1.5rem]"
                    />
                  }
                />
              </div>
              <div className="tw-flex tw-justify-center tw-mx-auto tw-mt-2">
                <div className="tw-inline">
                  <DiagnosticOnboardingPill
                    label="Take your time"
                    colorClass="tw-bg-lime-50 tw-border-lime-200"
                    icon={
                      <Timer
                        size={20}
                        className="tw-text-lime-500 tw-mx-2 tw-bg-lime-200 tw-rounded-full tw-p-1 tw-w-[1.5rem] tw-h-[1.5rem]"
                      />
                    }
                  />
                </div>
                <div className="tw-inline tw-ml-2">
                  <DiagnosticOnboardingPill
                    label="No pressure"
                    colorClass="tw-bg-amber-50 tw-border-amber-200"
                    icon={
                      <Sparkles
                        size={20}
                        className="tw-text-amber-500 tw-mx-2 tw-bg-amber-200 tw-rounded-full tw-p-1 tw-w-[1.5rem] tw-h-[1.5rem]"
                      />
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        ),
        btnLabel: `Let's get started!`,
        command: () => {
          // Guided tour walks this same screen without a real diagnostic session.
          if (!previewMode) {
            requestFullscreen();
          }
          setStepDiagnostic(DiagnosticSteps.ASSESSMENT);
        },
      },
    ];

    return (
      <Card
        className={`tw-w-[40rem] tw-h-[30rem] ${
          step === 0 ? 'dg-welcome-header-bg' : ''
        } !tw-px-0 tw-mt-[10vh] !tw-pb-0`}
      >
        <div className="tw-bg-white !tw-h-[23rem] tw-mt-8 tw-w-full tw-h-full tw-rounded-b-2xl">
          <div className="tw-px-3 tw-h-[16.5rem] tw-mb-5">
            {options[step].header}
            {options[step].content}
          </div>

          <div className="tw-flex tw-justify-center tw-w-full tw-pt-5">
            <Button onClick={options[step].command}>
              <div className="tw-flex tw-px-3 tw-justify-center">
                <span>{options[step].btnLabel}</span>
                <ArrowRight size={20} className="tw-ml-2" />
              </div>
            </Button>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <>
      {diagnosticState !== DiagnosticStateEnum.NEW ? (
        <ResumeDiagnosticCard
          refechSessionInfo={refechSessionInfo}
          diagnosticState={diagnosticState}
          setDiagnosticState={setDiagnosticState}
          setStepDiagnostic={setStepDiagnostic}
        />
      ) : (
        renderInnerContent()
      )}
    </>
  );
};
